import type { BlogPost } from './blogPosts'

// Deel twee van de blogartikelen: vormgeving, branches en techniek.

export const blogPostsPart2: BlogPost[] = [
  {
    slug: 'logo-laten-ontwerpen-waar-let-je-op',
    title: 'Logo laten ontwerpen: waar let je op?',
    category: 'Vormgeving',
    excerpt:
      'Een logo dat mooi is op een scherm kan onbruikbaar zijn op een bus. Dit zijn de vragen die je vooraf stelt.',
    metaDescription:
      'Een logo laten ontwerpen? Waar je op let bij varianten, leesbaarheid op klein formaat, bestandsformaten en eigendom, zodat je er jaren mee vooruit kunt.',
    keywords:
      'logo laten ontwerpen, logo laten maken, huisstijl logo, logo bestandsformaten',
    readingTime: 6,
    content: [
      {
        type: 'paragraph',
        content:
          'Een logo wordt vaak beoordeeld op de vraag of het mooi is. Dat is de minst nuttige vraag. Bruikbaarheid bepaalt of je er over vijf jaar nog mee vooruit kunt, en die bruikbaarheid zit in details die je bij de presentatie niet ziet.',
      },
      { type: 'heading', content: 'Vraag naar de varianten' },
      {
        type: 'paragraph',
        content:
          'Eén logo is nooit genoeg. Je hebt een liggende versie voor je website, een compacte versie voor een profielfoto, een uitvoering in één kleur voor een stempel of bedrijfskleding en een lichte versie voor donkere achtergronden. Krijg je alleen het hoofdlogo, dan zit je bij elke nieuwe toepassing weer bij de ontwerper.',
      },
      { type: 'heading', content: 'Test het klein en test het ver' },
      {
        type: 'list',
        items: [
          'Print het op zestien millimeter breed. Is het nog leesbaar, dan werkt het ook als favicon en op een pen.',
          'Bekijk het van vijf meter afstand. Zo ziet iemand je bus of je gevel.',
          'Zet het in grijstinten. Werkt het zonder kleur, dan werkt het overal.',
          'Leg het op een foto. Veel logo\u2019s vallen weg zodra er iets achter staat.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een logo hoeft niet uit te leggen wat je doet. Dat doet je bedrijfsnaam en je tekst eromheen.',
      },
      { type: 'heading', content: 'Welke bestanden je moet krijgen' },
      {
        type: 'paragraph',
        content:
          'Vraag altijd om een vectorbestand, want dat kun je oneindig vergroten zonder kwaliteitsverlies. Daarnaast png-bestanden met een transparante achtergrond voor digitaal gebruik en een svg voor je website. Krijg je alleen een jpg, dan kan een drukker er niets mee.',
      },
      { type: 'heading3', content: 'En van wie is het daarna?' },
      {
        type: 'paragraph',
        content:
          'Leg vast dat je het logo mag gebruiken waarvoor je wilt. Bij ons krijg je de huisstijl bovendien als Figma-document, waarin de varianten en kleuren klaarstaan zodat je zelf een nieuwe toepassing kunt maken zonder ons.',
      },
    ],
    relatedPosts: [
      'huisstijl-in-figma-uitgelegd',
      'visitekaartjes-die-blijven-liggen',
      'gevelreclame-en-bedrijfskleding',
    ],
  },
  {
    slug: 'huisstijl-in-figma-uitgelegd',
    title: 'Je huisstijl in Figma: wat je er zelf mee kunt',
    category: 'Vormgeving',
    excerpt:
      'De meeste bureaus leveren een pdf die je alleen kunt bekijken. Wij leveren een document waarin je kunt werken.',
    metaDescription:
      'Wat het betekent als je je huisstijl als Figma-document krijgt: kleuren, teksten en varianten zelf aanpassen zonder bij je ontwerper aan te kloppen.',
    keywords:
      'huisstijl figma, huisstijlhandboek, huisstijl zelf aanpassen, brand kit figma',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Bij de meeste bureaus krijg je na een huisstijltraject een map met eindbestanden en een pdf-handboek. Mooi om te zien, maar zodra je een adres wijzigt of een poster voor een actie nodig hebt, ga je terug naar de ontwerper. Dat kost tijd, geld en meestal een week wachten op iets dat in tien minuten kan.',
      },
      { type: 'heading', content: 'Wat een Figma-oplevering anders maakt' },
      {
        type: 'paragraph',
        content:
          'Figma is een ontwerpprogramma dat in je browser werkt, dus je hoeft niets te installeren. Wij leveren je huisstijl als een werkend document: je kleuren staan als vaste waarden vastgelegd, je logo staat er in alle varianten in, en de onderdelen die je vaak nodig hebt staan als sjabloon klaar.',
      },
      {
        type: 'list',
        items: [
          'Verander een kleur op één plek en alles wat die kleur gebruikt gaat mee.',
          'Pas een tekst aan in een bestaand sjabloon en exporteer een nieuwe versie als pdf of afbeelding.',
          'Kies een variant, bijvoorbeeld staand in plaats van liggend, zonder iets na te bouwen.',
          'Deel een link met een collega of met je drukker, zonder bestanden te mailen.',
        ],
      },
      {
        type: 'callout',
        content:
          'Je bent niet afhankelijk van je ontwerper voor elke kleine wijziging. Dat is het hele punt.',
      },
      { type: 'heading', content: 'Wat je er niet in doet' },
      {
        type: 'paragraph',
        content:
          'Een huisstijl compleet omgooien blijft ontwerpwerk, en dat moet je ook niet zelf willen. De grens is simpel: kleine wijzigingen binnen de vastgelegde stijl doe je zelf, nieuwe toepassingen die er nog niet zijn doen wij. Zo blijft het geheel consistent.',
      },
      { type: 'heading3', content: 'Voor wie dit vooral uitmaakt' },
      {
        type: 'paragraph',
        content:
          'Voor bedrijven die vaak iets nodig hebben: horeca met wisselende kaarten, sportscholen met acties, evenementenbureaus met per project ander materiaal. Wie eens per drie jaar iets laat maken, merkt er minder van.',
      },
    ],
    relatedPosts: [
      'logo-laten-ontwerpen-waar-let-je-op',
      'visitekaartjes-die-blijven-liggen',
      'gevelreclame-en-bedrijfskleding',
    ],
  },
  {
    slug: 'visitekaartjes-die-blijven-liggen',
    title: 'Visitekaarten die niet in de la verdwijnen',
    category: 'Vormgeving',
    excerpt:
      'Papier is niet dood, het wordt alleen slecht gebruikt. Wat een kaart wel moet doen.',
    metaDescription:
      'Waarom de meeste visitekaarten in een la belanden, en hoe je er een maakt die bewaard wordt: één duidelijke reden om te bellen, leesbare gegevens en de juiste afwerking.',
    keywords:
      'visitekaartjes laten maken, visitekaart ontwerp, drukwerk mkb, zakelijke visitekaart',
    readingTime: 4,
    content: [
      {
        type: 'paragraph',
        content:
          'Bij een hovenier, een installateur of een fysiotherapeut is de kaart die je achterlaat vaak het enige tastbare dat een klant van je heeft. Toch wordt hij meestal ontworpen als een verkleind logo met daaronder alles wat er nog bij paste.',
      },
      { type: 'heading', content: 'Eén reden om hem te bewaren' },
      {
        type: 'paragraph',
        content:
          'Zet op de achterkant iets dat de kaart nut geeft. Bij een hovenier het seizoen waarin hij weer langskomt, bij een installateur de datum van de volgende onderhoudsbeurt, bij een praktijk de oefening die je hebt meegegeven. Een kaart met een functie wordt niet weggegooid.',
      },
      {
        type: 'list',
        items: [
          'Houd de voorkant leeg genoeg. Naam, bedrijf, één manier om contact op te nemen.',
          'Gebruik minimaal negen punt lettergrootte. Een groot deel van je klanten leest niet meer zo scherp.',
          'Kies matte afwerking als er op geschreven moet worden. Op glanzend papier glijdt een pen weg.',
          'Zorg dat je telefoonnummer klikbaar oogt en klopt met wat er op je site staat.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een kaart hoeft niet je hele aanbod te vertellen. Hij moet één ding doen: zorgen dat iemand je terugvindt.',
      },
      { type: 'heading3', content: 'Over het drukken' },
      {
        type: 'paragraph',
        content:
          'Wij zijn geen drukkerij, maar we regelen het drukwerk via een vaste partner als je daarom vraagt. Wil je naar je eigen drukker, dan krijg je de drukklare bestanden mee met de juiste maten en aflopen. Dat scheelt gedoe bij de aanlevering.',
      },
    ],
    relatedPosts: [
      'logo-laten-ontwerpen-waar-let-je-op',
      'huisstijl-in-figma-uitgelegd',
      'gevelreclame-en-bedrijfskleding',
    ],
  },
  {
    slug: 'gevelreclame-en-bedrijfskleding',
    title: 'Gevelreclame en bedrijfskleding: je merk buiten de website',
    category: 'Vormgeving',
    excerpt:
      'Voor een vakbedrijf is de bus vaak het meest geziene reclamebord. Zo gebruik je dat goed.',
    metaDescription:
      'Gevelreclame, busbelettering en bedrijfskleding ontwerpen: waar je op let bij leesbaarheid, afstand en materiaal, zodat je merk buiten je website ook klopt.',
    keywords:
      'gevelreclame ontwerp, bedrijfskleding ontwerp, busbelettering, bedrijfsauto belettering',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Een renovatiebedrijf dat vier dagen per week in een straat staat, wordt daar meer gezien dan op internet. Toch krijgt de bus vaak het logo dat voor een website is gemaakt, in een maat waarop niemand het kan lezen.',
      },
      { type: 'heading', content: 'Lezen op afstand is een ander vak' },
      {
        type: 'paragraph',
        content:
          'Op tien meter afstand ziet iemand maximaal drie dingen: wat je doet, hoe je heet en hoe hij je bereikt. In die volgorde. Je vakgebied hoort dus groter dan je logo, en één telefoonnummer of website is genoeg. Alle iconen, kwaliteitskeurmerken en slogans die erbij komen halen de leesbaarheid onderuit.',
      },
      {
        type: 'list',
        items: [
          'Kies één kleurcombinatie met veel contrast. Donkerblauw op wit werkt beter dan grijs op grijs.',
          'Zet je website zonder https en zonder www. Korter is leesbaarder.',
          'Bij een bus: houd rekening met deuren, ruiten en portieren waar niets doorheen mag lopen.',
          'Bij een gevel: check de regels van je gemeente voordat je iets laat maken.',
        ],
      },
      {
        type: 'callout',
        content:
          'Zet een concept op je telefoon en loop naar de overkant van de straat. Wat je dan niet leest, leest je klant ook niet.',
      },
      { type: 'heading3', content: 'Bedrijfskleding' },
      {
        type: 'paragraph',
        content:
          'Bij kleding gaat het om herkenning bij de voordeur. Een logo op de borst en je bedrijfsnaam groot op de rug is voldoende. Belangrijker dan het ontwerp is dat het bij elke bestelling identiek is, en daarvoor moet je leverancier de juiste bestanden en kleurcodes hebben. Die leggen we in het huisstijldocument vast.',
      },
    ],
    relatedPosts: [
      'logo-laten-ontwerpen-waar-let-je-op',
      'huisstijl-in-figma-uitgelegd',
      'hovenier-projecten-laten-zien',
    ],
  },
  {
    slug: 'website-fysiotherapiepraktijk-checklist',
    title: 'Checklist: wat hoort er op de website van een fysiotherapiepraktijk?',
    category: 'Per branche',
    excerpt:
      'Een praktijk heeft te veel om te vertellen. Dat is precies het probleem dat je met structuur oplost.',
    metaDescription:
      'Wat hoort er op de website van een fysiotherapiepraktijk? Een checklist met behandelpagina\u2019s, patiëntenportaal, vergoedingen, vacatures en de vragen die patiënten stellen.',
    keywords:
      'website fysiotherapiepraktijk, praktijkwebsite fysiotherapie, website fysiotherapeut, patiëntenportaal koppelen',
    readingTime: 6,
    content: [
      {
        type: 'paragraph',
        content:
          'Een fysiotherapiepraktijk heeft een probleem dat de meeste bedrijven niet kennen: er is te veel om te vertellen. Met tien tot vijftien specialisaties, meerdere netwerken en een team met verschillende aandachtsgebieden raakt een bezoeker verdwaald voordat hij bij zijn eigen klacht is.',
      },
      { type: 'heading', content: 'Een eigen pagina per behandeling' },
      {
        type: 'paragraph',
        content:
          'Dit is het belangrijkste punt. Iemand die zoekt op dry needling, oedeemfysiotherapie of revalidatie na een knieoperatie moet direct op die behandeling landen. Eén verzamelpagina met alles onder elkaar rankt op geen van die termen. Beschrijf per behandeling de klacht, wat een traject inhoudt en wie het bij jullie doet.',
      },
      { type: 'heading', content: 'De checklist' },
      {
        type: 'list',
        items: [
          'Een pagina per behandeling of specialisatie, in de woorden van de patiënt.',
          'Aanmelden of een afspraak maken, het liefst gekoppeld aan je praktijksoftware.',
          'Duidelijkheid over vergoedingen en verwijzing, want dat is de meest gestelde vraag.',
          'Je team met foto en aandachtsgebied per therapeut.',
          'Praktische informatie: openingstijden, parkeren, toegankelijkheid en de ingang.',
          'De netwerken waarbij je bent aangesloten, met uitleg wat dat voor de patiënt betekent.',
          'Vacatures op je eigen site, met een eigen pagina per functie.',
          'Antwoorden op de vragen die aan de balie het vaakst gesteld worden.',
        ],
      },
      {
        type: 'callout',
        content:
          'Elk telefoontje over iets dat online had gekund is een teken dat de site een pagina mist.',
      },
      { type: 'heading3', content: 'Waarom een portaal loont' },
      {
        type: 'paragraph',
        content:
          'Een koppeling met je praktijksoftware laat patiënten hun afspraken en vragenlijsten zelf regelen, op het moment dat het hun uitkomt. Dat scheelt je balie telefoontjes en het geeft de patiënt het gevoel dat de praktijk bij de tijd is. Zie de case van Fysiotherapiepraktijk CG voor hoe dat er in de praktijk uitziet.',
      },
    ],
    relatedPosts: [
      'hoeveel-paginas-heeft-mijn-website-nodig',
      'sportschool-meer-proeflessen',
      'uitzendbureau-twee-doelgroepen',
    ],
  },
  {
    slug: 'sportschool-meer-proeflessen',
    title: 'Meer proeflessen uit je website halen als sportschool',
    category: 'Per branche',
    excerpt:
      'Iemand die op je site komt heeft de drempel al half over. Meestal ligt de rest aan onduidelijkheid.',
    metaDescription:
      'Hoe je als sportschool meer proeflessen en inschrijvingen uit je website haalt: duidelijke tarieven, een lesrooster dat klopt en één heldere vervolgstap.',
    keywords:
      'website sportschool, meer leden sportschool, proefles aanvragen, marketing fitnesscentrum',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Wie op de site van een sportschool komt, is meestal al bijna over de streep. Hij zoekt geen inspiratie, hij zoekt een reden om niet af te haken. Op de meeste sites vindt hij die reden ruimschoots: geen prijzen, een rooster van vorig jaar en een contactformulier zonder duidelijkheid.',
      },
      { type: 'heading', content: 'Zet je tarieven erop' },
      {
        type: 'paragraph',
        content:
          'Prijzen weglaten om het gesprek aan te gaan werkt hier niet. Mensen vergelijken drie sportscholen en de een zonder prijs valt af. Zet de abonnementsvormen erop, met wat er wel en niet bij zit en of er inschrijfgeld is.',
      },
      {
        type: 'list',
        items: [
          'Een actueel rooster, ook op mobiel goed leesbaar. Een verlopen rooster kost je meer dan geen rooster.',
          'Eén duidelijke vervolgstap: de proefles aanvragen. Niet ook nog bellen, mailen, appen en langskomen.',
          'Echte foto\u2019s van je zaal, niet van een Amerikaanse gym. Mensen willen weten waar ze binnenkomen.',
          'Vertel wat er bij een eerste bezoek gebeurt. De onzekerheid daarover is de grootste drempel.',
          'Zet je begeleiding en je specialisaties erbij, zoals herstel na blessure of trainen op leeftijd.',
        ],
      },
      {
        type: 'callout',
        content:
          'De meeste afhakers zijn geen twijfelaars over sport. Het zijn mensen die niet weten hoe hun eerste bezoek eruitziet.',
      },
      { type: 'heading3', content: 'De vindbaarheid eronder' },
      {
        type: 'paragraph',
        content:
          'Zoekopdrachten in deze branche zijn bijna altijd lokaal: sportschool met de plaatsnaam erbij, of een specifieke les. Zorg dat je plaats in je titels en teksten staat en dat je Google Bedrijfsprofiel je openingstijden en foto\u2019s compleet heeft. Dat levert vaker een proefles op dan een campagne.',
      },
    ],
    relatedPosts: [
      'lokaal-gevonden-worden-noord-holland',
      'website-fysiotherapiepraktijk-checklist',
      'contactformulier-dat-aanvragen-oplevert',
    ],
  },
  {
    slug: 'hovenier-projecten-laten-zien',
    title: 'Als hovenier je projecten laten zien zonder dure fotoshoot',
    category: 'Per branche',
    excerpt:
      'Je telefoon is genoeg. Het gaat om het moment waarop je hem pakt en wat je erbij vertelt.',
    metaDescription:
      'Als hovenier of renovatiebedrijf je werk online laten zien: hoe je met je telefoon bruikbare voor-en-na foto\u2019s maakt en er projecten van maakt die aanvragen opleveren.',
    keywords:
      'website hovenier, projecten laten zien hovenier, portfolio hovenier, voor en na foto tuin',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'In de groenbranche en de bouw wordt je opdracht gegund op basis van wat mensen al gemaakt zien. Toch staan er op de meeste sites drie foto\u2019s van een tuin waarvan niemand weet of hij die zelf heeft aangelegd.',
      },
      { type: 'heading', content: 'Maak van elk project een verhaal van drie foto\u2019s' },
      {
        type: 'paragraph',
        content:
          'Voor, tijdens en na. De vooraf-foto is de belangrijkste en wordt bijna altijd vergeten, want op dat moment denk je nog niet aan je website. Maak er een gewoonte van: sta je voor het eerst in een tuin, dan maak je vijf foto\u2019s vanaf een vast punt. Na oplevering ga je op datzelfde punt staan.',
      },
      {
        type: 'list',
        items: [
          'Fotografeer bij bewolkt weer of vroeg in de avond. Volle zon geeft harde schaduwen.',
          'Sta stil en fotografeer op ooghoogte, recht en niet schuin omhoog.',
          'Ruim gereedschap en kruiwagens weg voor de na-foto. Dat is het halve werk.',
          'Vraag de klant om toestemming en om één zin over hoe het bevalt. Die zin is je review.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een eerlijke telefoonfoto van een tuin uit je eigen dorp verkoopt beter dan een perfecte stockfoto uit Californië.',
      },
      { type: 'heading', content: 'Wat je erbij schrijft' },
      {
        type: 'paragraph',
        content:
          'Drie regels zijn genoeg: waar het was, wat de vraag was en wat je hebt gedaan. Noem de plaats, want daarmee wordt zo\u2019n project ook een pagina die lokaal vindbaar is. Tien projecten in tien dorpen is een van de sterkste dingen die je kunt hebben.',
      },
    ],
    relatedPosts: [
      'lokaal-gevonden-worden-noord-holland',
      'gevelreclame-en-bedrijfskleding',
      'teksten-voor-je-website-schrijven',
    ],
  },
  {
    slug: 'uitzendbureau-twee-doelgroepen',
    title: 'Een uitzendbureau bedient twee doelgroepen. Zo doe je dat op één site',
    category: 'Per branche',
    excerpt:
      'Kandidaten en opdrachtgevers willen tegengestelde dingen. Eén site die het voor beide probeert werkt voor niemand.',
    metaDescription:
      'Een uitzendbureau spreekt kandidaten en opdrachtgevers aan. Hoe je dat met twee heldere routes op één website oplost, zonder dat de een de ander in de weg zit.',
    keywords:
      'website uitzendbureau, vacatures op eigen site, kandidaten werven online, uitzendbureau marketing',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Een uitzendbureau heeft twee soorten bezoekers met tegengestelde belangen. De kandidaat wil weten wat het werk oplevert en hoe snel hij kan beginnen. De opdrachtgever wil weten of je op tijd mensen kunt leveren en wat het kost. Een homepage die beide tegelijk toespreekt, spreekt niemand aan.',
      },
      { type: 'heading', content: 'Splits bovenaan, niet halverwege' },
      {
        type: 'paragraph',
        content:
          'Zet direct bovenaan twee duidelijke routes: ik zoek werk, en ik zoek personeel. Vanaf dat punt zijn het twee aparte delen van je site met hun eigen taal, hun eigen bewijs en hun eigen formulier. Dat is geen dubbel werk, dat is het voorkomen van een site die voor iedereen half klopt.',
      },
      { type: 'heading3', content: 'Wat de kandidatenkant nodig heeft' },
      {
        type: 'list',
        items: [
          'Een eigen pagina per vacature, met plaats, uren en een indicatie van het loon.',
          'Een korte sollicitatieroute. Naam, nummer en welke vacature is genoeg om te bellen.',
          'Uitleg over hoe het werkt: contract, uitbetaling en wie je begeleidt.',
        ],
      },
      { type: 'heading3', content: 'Wat de opdrachtgeverskant nodig heeft' },
      {
        type: 'list',
        items: [
          'De sectoren waarin je levert, met een pagina per sector.',
          'Hoe snel je kunt schakelen bij een piek, en wat je regelt rond wetgeving en verzekering.',
          'Bewijs uit de regio: welke bedrijven je bedient en hoe lang al.',
        ],
      },
      {
        type: 'callout',
        content:
          'Vacatures op je eigen site zijn ook vindbaarheid. Elke vacature is een pagina die op werk plus plaatsnaam kan ranken.',
      },
      {
        type: 'paragraph',
        content:
          'Zet vacatures dus niet alleen op een vacaturebank waar je concurrent ernaast staat. Op je eigen site houd je het contact, en verzamel je gaandeweg een archief van functies waarop mensen zoeken.',
      },
    ],
    relatedPosts: [
      'hoeveel-paginas-heeft-mijn-website-nodig',
      'website-fysiotherapiepraktijk-checklist',
      'contactformulier-dat-aanvragen-oplevert',
    ],
  },
  {
    slug: 'zakelijke-email-op-je-eigen-domein',
    title: 'Zakelijke e-mail op je eigen domein: waarom gmail je geld kost',
    category: 'Techniek en beheer',
    excerpt:
      'Mailen vanaf een gratis adres kost je vertrouwen bij precies die klanten die je wilt hebben.',
    metaDescription:
      'Waarom je als bedrijf mailt vanaf je eigen domein en niet vanaf gmail of hotmail, wat het kost om over te stappen en hoe je zorgt dat je mail niet in spam belandt.',
    keywords:
      'zakelijke e-mail eigen domein, e-mail op eigen domein, gmail zakelijk gebruiken, mail in spam',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Mailen vanaf een gratis adres is gratis in de directe kosten. De rekening komt later: bij de opdrachtgever die je offerte naast die van een concurrent legt, en bij de mail die in de spammap belandt.',
      },
      { type: 'heading', content: 'Wat het je kost aan vertrouwen' },
      {
        type: 'paragraph',
        content:
          'Een adres op je eigen domein zegt dat je bedrijf een bedrijf is. Bij particulieren maakt het minder uit, maar zodra je zakelijke opdrachten wilt, weegt het mee. Bij grotere opdrachtgevers en gemeenten is het soms zelfs een formeel struikelpunt.',
      },
      { type: 'heading', content: 'Wat er technisch onder moet zitten' },
      {
        type: 'paragraph',
        content:
          'Een eigen adres alleen is niet genoeg. Er hoort een aantal instellingen bij die vastleggen dat jij als enige namens jouw domein mag mailen. Zijn die er niet, dan komt je mail vaker in spam terecht en kan iemand anders makkelijker uit jouw naam mailen. Dit is eenmalig werk en daarna heb je er geen omkijken naar.',
      },
      {
        type: 'list',
        items: [
          'Kies één vast schema voor adressen, bijvoorbeeld voornaam apenstaartje jouwbedrijf punt nl.',
          'Zet een algemeen adres op voor info en een apart adres voor facturen.',
          'Laat de beveiligingsinstellingen voor je domein goed inrichten en controleer ze na een verhuizing.',
          'Zorg dat het formulier op je site vanaf je eigen domein verstuurt, anders verdwijnen aanvragen.',
        ],
      },
      {
        type: 'callout',
        content:
          'De meest gemiste aanvragen komen niet van een kapot formulier maar van mail die nooit is aangekomen.',
      },
      { type: 'heading3', content: 'Wat de overstap inhoudt' },
      {
        type: 'paragraph',
        content:
          'Je oude mail en contacten kunnen mee, en je oude adres kun je een tijd laten doorsturen zodat niemand je kwijtraakt. Reken op een halve dag waarin het geregeld wordt, meestal zonder dat je iets merkt.',
      },
    ],
    relatedPosts: [
      'contactformulier-dat-aanvragen-oplevert',
      'website-onderhoud-waarom-nodig',
      'website-sneller-maken',
    ],
  },
  {
    slug: 'website-onderhoud-waarom-nodig',
    title: 'Website-onderhoud: wat er gebeurt als je het laat liggen',
    category: 'Techniek en beheer',
    excerpt:
      'Een website is geen schilderij aan de muur. Zonder onderhoud gaat hij niet stuk, tot hij het wel doet.',
    metaDescription:
      'Wat website-onderhoud inhoudt en waarom het nodig is: updates, back-ups, beveiliging en monitoring, plus wat een verwaarloosde website je uiteindelijk kost.',
    keywords:
      'website onderhoud, wordpress onderhoud, back-ups website, website beveiliging mkb',
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        content:
          'Onderhoud is de post die ondernemers het liefst wegstrepen. Begrijpelijk: je ziet er niets van terug, tot de dag dat je site onbereikbaar is of dat er via een gat in een verouderde plug-in wordt meegekeken.',
      },
      { type: 'heading', content: 'Wat er onder onderhoud valt' },
      {
        type: 'list',
        items: [
          'Updates van je systeem, je thema en je plug-ins, en controleren of de site daarna nog goed werkt.',
          'Dagelijkse back-ups die op een andere plek staan dan je site zelf.',
          'Beveiliging: een firewall, scans op malware en het dichten van bekende lekken.',
          'Monitoring, zodat wij merken dat je site offline is en niet jij van een klant.',
          'Kleine inhoudelijke wijzigingen, zoals nieuwe openingstijden of een aangepaste prijs.',
        ],
      },
      { type: 'heading', content: 'Wat het kost als het blijft liggen' },
      {
        type: 'paragraph',
        content:
          'Een verwaarloosde site gaat zelden in één keer stuk. Hij wordt langzamer, formulieren werken niet meer, het contactformulier stuurt naar een oud adres, en op een gegeven moment werkt een update niet meer omdat er drie jaar aan achterstand ligt. Herstel kost dan meer dan drie jaar onderhoud.',
      },
      {
        type: 'callout',
        content:
          'Vraag bij een aanbieding altijd of updates, back-ups en beveiliging erin zitten. Dat verschilt sterk per partij.',
      },
      { type: 'heading3', content: 'Wat je zelf kunt controleren' },
      {
        type: 'paragraph',
        content:
          'Vul één keer per maand je eigen formulier in vanaf je telefoon en kijk of de mail aankomt. Klik je belangrijkste pagina\u2019s door. Controleer of je openingstijden nog kloppen. Dat is vijf minuten werk en het vangt de meeste problemen op voordat een klant ze tegenkomt.',
      },
    ],
    relatedPosts: [
      'website-sneller-maken',
      'zakelijke-email-op-je-eigen-domein',
      'wat-kost-een-website-laten-maken',
    ],
  },
]
